import { useEffect, useRef, useState } from 'react';

/**
 * 이 정도 이상 줄어들면 소프트 키보드로 본다. 주소창 숨김/표시 같은 작은 변화와 구분하기 위한 값.
 */
const KEYBOARD_SHRINK_THRESHOLD = 100;

/**
 * `position: fixed` 요소가 실제로 그릴 수 있는 높이.
 *
 * 두 제약의 교집합이다.
 * - `visualViewport.height` — 키보드를 뺀, 눈에 보이는 높이
 * - `innerHeight - visualViewport.offsetTop` — fixed 요소는 **레이아웃 뷰포트**에 붙으므로,
 *   브라우저가 포커스 위치로 화면을 밀어낸(offsetTop) 만큼은 쓸 수 없다
 *
 * 실측(키보드 노출): `inner=470 vv=330 off=171` → 실제 가용 높이는 `470 - 171 = 299`.
 */
const readVisibleHeight = (): number => {
  if (typeof window === 'undefined') return 0;

  const visualViewport = window.visualViewport;
  if (!visualViewport) return window.innerHeight;

  return Math.min(
    visualViewport.height,
    window.innerHeight - visualViewport.offsetTop,
  );
};

/**
 * 소프트 키보드로 화면이 좁아진 상태인지(웹).
 *
 * **관측한 최대 높이 대비 축소량**으로 판단한다. 키보드가 레이아웃 뷰포트를 줄이는지
 * (`innerHeight` 변화) 비주얼 뷰포트만 줄이는지가 브라우저·OS 버전마다 달라, 어느 쪽이든
 * "그릴 수 있는 높이가 갑자기 줄었다"로 감지하는 것이 안전하다.
 */
export const useIsSoftKeyboardVisible = (): boolean => {
  const [visibleHeight, setVisibleHeight] =
    useState<number>(readVisibleHeight);
  const maxHeightRef = useRef(visibleHeight);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onChange = () => setVisibleHeight(readVisibleHeight());
    const visualViewport = window.visualViewport;

    window.addEventListener('resize', onChange);
    visualViewport?.addEventListener('resize', onChange);
    // 브라우저가 포커스 위치로 화면을 밀어내면 resize 없이 scroll 만 오기도 한다.
    visualViewport?.addEventListener('scroll', onChange);

    return () => {
      window.removeEventListener('resize', onChange);
      visualViewport?.removeEventListener('resize', onChange);
      visualViewport?.removeEventListener('scroll', onChange);
    };
  }, []);

  if (visibleHeight > maxHeightRef.current) {
    maxHeightRef.current = visibleHeight;
  }

  return maxHeightRef.current - visibleHeight > KEYBOARD_SHRINK_THRESHOLD;
};
